import React, { FC } from 'react';
import * as yup from 'yup';
import { Flex, Input, List, Text } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { AuthStatus, useAuthDispatch, useAuthState } from '../../contexts/AuthContext';
import { Field } from '../ui/field';
import { Button } from '../ui/button';

type ForgotPasswordFormProps = {
  callback({ email }: { email: string }): void;
};

const ForgotPasswordForm: FC<ForgotPasswordFormProps> = ({ callback }) => {
  const { logEvent } = useAnalytics();
  const { sessionStatus } = useAuthState();
  const { resetPassword, dispatch } = useAuthDispatch();
  const { t } = useTranslation('auth');
  const forgotPasswordSchema = yup.object({
    email: yup.string().email(t('validation.validEmail')).required(t('validation.emailRequired'))
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: {
      email: ''
    }
  });

  const onSubmit = async (evt: any) => {
    const { email } = evt;

    logEvent({
      action: 'forgot_password',
      category: 'Sign in'
    });

    await resetPassword({ email });

    callback({ email });
  };

  const onCancel = async () => {
    dispatch({ type: 'clearSession' });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Email */}
      <Field required label={t('forgot.email')} invalid={!!errors.email} errorText={errors.email?.message}>
        <Controller
          name='email'
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              required
              id='email'
              type='email'
              autoComplete='email'
              placeholder={t('forgot.emailPlaceholder')}
            />
          )}
        />
        <Text color='gray.400' mt={4} fontSize='xs'>
          {t('forgot.howToReset')}
        </Text>
        <List.Root px='4' pt='2' color='gray.400' fontSize='xs'>
          <List.Item>{t('forgot.step1')}</List.Item>
          <List.Item>{t('forgot.step2')}</List.Item>
          <List.Item>{t('forgot.step3')}</List.Item>
          <List.Item>{t('forgot.step4')}</List.Item>
        </List.Root>
      </Field>

      <Flex align='center' justify='flex-start' mt='8'>
        <Button variant='ghost' fontSize='sm' onClick={onCancel}>
          {t('forgot.cancel')}
        </Button>
      </Flex>

      <Button
        type='submit'
        mt='8'
        w='full'
        loading={isSubmitting || sessionStatus === AuthStatus.PENDING_RESET_PASSWORD}
        disabled={!!errors.email}
      >
        {t('forgot.submit')}
      </Button>
    </form>
  );
};

export default ForgotPasswordForm;
